/**
 * SAP CONFIDENTIAL 
 *
 * This software is confidential proprietary software licensed by SAP SE 
 * and is not to be used, duplicated or disclosed to anyone without the 
 * prior written permission of SAP. 
 *
 * (C) 2020 Copyright SAP SE.
 */
import { Injectable } from '@angular/core';
import moment from 'moment';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Calendar, PlanPeriod, SavePlanCalendar } from '../model/calendar';

export const PERIOD_TYPES = {
    BIWEEKLY: 'BI_WEEK',
    MONTHLY: 'MONTH',
    QUARTERLY: 'QUARTER',
    WEEKLY: 'WEEK'
}

export const MONTHS = moment.months();

@Injectable()
export class PlanPeriodsService {
    private envUrl: string = environment.host;
    private url: string = environment.host + '/plan';
    private companyUrl: string = environment.host + '/company/';

    constructor(private http: HttpClient) { }

    public createDefaultPeriodData(type: string) {
        //Set current date to 1st day of year
        const currentMom = moment().set('month', 0).set('date', 1);
        const currentYear = moment().get('year');
        const arr: Array<any> = [];
        if (type == PERIOD_TYPES.BIWEEKLY) {
            for (let i = 0; i < MONTHS.length; i++) {

                const cal1 = new Calendar();
                const cal2 = new Calendar();

                //Get Part1 of Month
                //                cal1.name = MONTHS[i] + ', Part 1';
                if (i < 9) {
                    cal1.name = currentYear + '0' + (i + 1) + ', Part 1';
                } else {
                    cal1.name = currentYear + '' + (i + 1) + ', Part 1';
                }
                cal1.type = type;
                cal1.start = this.getDateObj(currentMom);
                currentMom.set('date', 14);
                cal1.end = this.getDateObj(currentMom);
                cal1.commissionable = false;
                arr.push(cal1);

                //Get part2 of Month
                currentMom.set('date', 15);
                //                cal2.name = MONTHS[i] + ', Part 2';
                if (i < 9) {
                    cal2.name = currentYear + '0' + (i + 1) + ', Part 2';
                } else {
                    cal2.name = currentYear + '' + (i + 1) + ', Part 2';
                }
                cal2.type = type;
                cal2.start = this.getDateObj(currentMom);
                currentMom.add(1, 'month');
                currentMom.set('date', 1);
                currentMom.subtract(1, 'days');
                cal2.end = this.getDateObj(currentMom);
                cal2.commissionable = false;
                arr.push(cal2);

                //Move to next month, day 1
                currentMom.add(1, 'month');
                currentMom.set('date', 1);

            }
        } else if (type == PERIOD_TYPES.MONTHLY) {
            for (let i = 0; i < MONTHS.length; i++) {
                const cal = new Calendar();

                //Set 1st of Month & add to array
                cal.name = MONTHS[i];
                /*if (i < 9) {
                    cal.name = currentYear + '0' + (i + 1);
                } else {
                    cal.name = currentYear + '' + (i + 1);
                }*/
                cal.type = type;
                cal.start = this.getDateObj(currentMom);
                currentMom.add(1, 'month');
                currentMom.set('date', 1);
                currentMom.subtract(1, 'days');
                cal.end = this.getDateObj(currentMom);
                cal.commissionable =false;
                arr.push(cal);

                //Move to next month, day 1
                currentMom.add(1, 'month');
                currentMom.set('date', 1);
            }
        } else if (type == PERIOD_TYPES.QUARTERLY) {
            for (let i = 0; i < 4; i++) {
                const cal = new Calendar();

                //Set to 1st of Quarter & add to array
                //                cal.name = 'Quarter' + ' ' + (i + 1);
                cal.name = currentYear + '0' + (i + 1);
                cal.type = type;
                cal.start = this.getDateObj(currentMom);
                currentMom.add(3, 'month');
                currentMom.subtract(1, 'days');
                cal.end = this.getDateObj(currentMom);
                cal.commissionable =false;
                arr.push(cal);

                //Move to next month, day 1
                currentMom.add(1, 'month');
                currentMom.set('date', 1);
            }
        } else if (type == PERIOD_TYPES.WEEKLY) {
            for (let i = 0; i < 52; i++) {
                const cal = new Calendar();

                //Increment to 6 days add to end date
                //                cal.name = 'Week' + ' ' + (i + 1);
                if (i < 9) {
                    cal.name = currentYear + '0' + (i + 1);
                } else {
                    cal.name = currentYear + '' + (i + 1);
                }
                cal.type = type;
                cal.start = this.getDateObj(currentMom);
                currentMom.add(6, 'days');
                cal.end = this.getDateObj(currentMom);
                cal.commissionable = false;
                arr.push(cal);

                //Move 1 day
                currentMom.add(1, 'days');
            }

            //set last day of last week to last date of year
            arr[51].end = this.getDateObj(currentMom.set('date', 31).set('month', 11).set('year', currentYear));

        }

        return arr;

    }

    public getDateObj(currentMom: any) {
        return { date: { year: currentMom.get('year'), month: currentMom.get('month') + 1, day: currentMom.get('date') } };
    }

    public getDateObjFromUTC(utc: any) {
        const date = new Date(utc);
        const mom = moment().set('year', date.getFullYear()).set('month', date.getMonth()).set('date', date.getDate());
        return this.getDateObj(mom);
    }

    public getDateObjFromUTCDate(utc: any) {
        const date = new Date(utc);
        return date;
    }

    public prepareDateValue(obj: any) {
        const day = obj.day,
            month = obj.month,
            year = obj.year,
            date = new Date(month + '/' + day + '/' + year);
        return date;
    }

    public getPeriodDefinition(region: any) {
        let url;
        if (region) {
            url = environment.host + '/perioddefinition?regionCode=' + region;
        } else {
            url = environment.host + '/perioddefinition';
        }
        return this.http.get(url.toString()).toPromise();
    }

    public getCurrentPeriod(args: any, regionCode: string) {
        const url = regionCode ? (environment.host + '/currentperiod?regionCode=' + regionCode) : (environment.host + '/currentperiod');
        if (args && args.hideLoader) {
            // const headers = new Headers();
            // headers.append('hideLoader', 'true');
            // const options = new RequestOptions({ headers: headers });
            return this.http.get(url.toString(), { headers: { 'hideLoader': 'true' } }).toPromise();
        } else {
            return this.http.get(url.toString()).toPromise();
        }
    }

    public getRegions() {
        const url = environment.host + '/company';
        return this.http.get(url.toString()).toPromise();
    }

    public getCompanyGenConfiguration(companyId: any) {
        return this.http.get(this.companyUrl.toString() + companyId + '/configuration').toPromise();
    }

    public getPeriod(args?: any) {
        const url = environment.host + '/period';
        if (args && args.hideLoader) {
            // const headers = new Headers();
            // headers.append('hideLoader', 'true');
            // const options = new RequestOptions({ headers: headers });
            return this.http.get(url.toString(), { observe: 'body' }).toPromise();
            // const httpHeaders = new HttpHeaders()
            //              .set('hideLoader', 'true');
            // return this.http.get(url.toString(), {
            //     observe: 'body',
            //     headers: httpHeaders
            // }).toPromise();
        } else {
            return this.http.get(url.toString()).toPromise();
        }
    }

    public getPeriodBasedOnModule(moduleName: any, companyCode: any) {
        let url = environment.host + '/period?planPeriodRequest='+moduleName;
        if(companyCode){
            url = url+'&companyCode=' + companyCode;
        }
        return this.http.get(url.toString()).toPromise();
    } 

    public createPeriodDefinition(periodObj: any, region: any) {
        const period: SavePlanCalendar = new SavePlanCalendar();
        period.name = periodObj.name;
        const date = this.prepareDateValue(periodObj.calenderStartDate.date);
        period.startMonth = (date.getMonth() + 1);
        period.startDay = date.getDate();
        period.serviceDay= periodObj.serviceDay;
        period.serviceMonth = periodObj.serviceMonth;
        period.returnDay= periodObj.returnDay;
        period.returnMonth = periodObj.returnMonth;
        period.deliveryDay= periodObj.deliveryDay;
        period.deliveryMonth = periodObj.deliveryMonth;
        period.startTime = moment(periodObj.startTime).format('HH:mm');
        period.endTime = moment(periodObj.endTime).format('HH:mm');
        period.timeZone = periodObj.timeZone;

        if (periodObj.calendarInstances && periodObj.calendarInstances.length) {
            let periods: any = [];
            for (const item of periodObj.calendarInstances) {
                periods = periods.concat(item);
            }
            if (periods.length) {
                periods.forEach(function (period: any, idx: any) {
                    period.orderId = idx + 1;
                });
                const lastPeriod = periods[periods.length - 1];
                const lastPeriodDateEntity = this.prepareDateValue(lastPeriod.end.date)
                period.endMonth = (lastPeriodDateEntity.getMonth() + 1);
                period.endDay = lastPeriodDateEntity.getDate();
                period.orderId=0;
                period.periods = periods.map(this.postDateMap);
            }
        }
        if (region) {
            return this.http.post((environment.host + '/perioddefinition?regionCode=' + region).toString(), period).toPromise();
        } else {
            return this.http.post((environment.host + '/perioddefinition').toString(), period).toPromise();
        }
    }

    public updatePeriodDefinition(periodObj: any, region: any) {
        const period: SavePlanCalendar = new SavePlanCalendar();
        period.name = periodObj.name;
        const date = this.prepareDateValue(periodObj.calenderStartDate.date);
        period.startMonth = (date.getMonth() + 1);
        period.startDay = date.getDate();
        period.serviceDay= periodObj.serviceDay;
        period.serviceMonth = periodObj.serviceMonth;
        period.returnDay= periodObj.returnDay;
        period.returnMonth = periodObj.returnMonth;
        period.deliveryDay= periodObj.deliveryDay;
        period.deliveryMonth = periodObj.deliveryMonth;
        period.startTime = moment(periodObj.startTime).format('HH:mm');
        period.endTime = moment(periodObj.endTime).format('HH:mm');
        period.timeZone = periodObj.timeZone;

        period.id = periodObj.id;
        if (periodObj.calendarInstances && periodObj.calendarInstances.length) {
            let periods: any = [];
            for (const item of periodObj.calendarInstances) {
                periods = periods.concat(item);
            }
            if (periods.length) {
                periods.forEach(function (period: any, idx: any) {
                    period.orderId = idx + 1;
                });
                const lastPeriod = periods[periods.length - 1];
                const lastPeriodDateEntity = this.prepareDateValue(lastPeriod.end.date)
                period.endMonth = (lastPeriodDateEntity.getMonth() + 1);
                period.endDay = lastPeriodDateEntity.getDate();
                period.orderId=0;
                period.periods = periods.map(this.postDateMap);
            }
        }
        if (region) {
            return this.http.put((environment.host + '/perioddefinition?regionCode=' + region).toString(), period).toPromise();
        } else {
            return this.http.put((environment.host + '/perioddefinition').toString(), period).toPromise();
        }
    }

    public postDateMap(item: any, index: any) {
        const obj: PlanPeriod = new PlanPeriod();
        if (item.id && item.id != 0) {
            obj.id = item.id;
        }
        obj.name = item.name;
        obj.startDay = item.start.date.day;
        obj.startMonth = item.start.date.month;
        obj.endDay = item.end.date.day;
        obj.endMonth = item.end.date.month;
        obj.orderId = item.orderId;
        obj.commissionable = item.commissionable;
        obj.startYear = item.startYear;
        obj.endYear = item.endYear;
       /* obj.type = item.type;
        obj.start = new Date(item.start.date.year, item.start.date.month - 1, item.start.date.day).getTime();
        obj.end = new Date(item.end.date.year, item.end.date.month - 1, item.end.date.day, 23, 59, 59).getTime();
        */return obj;
    }

    public getDateMap(item: any, index: any) {
        const obj: Calendar = new Calendar();
        obj.id = item.id;
        obj.name = item.name;
        obj.type = item.type;
        const startMonth = item.startMonth,
            startDay = item.startDay,
            currentYear = new Date().getFullYear(),
            startDate = startMonth + '/' + startDay + '/' + currentYear;
        obj.start = this.getDateObjFromUTC(startDate);
        obj.startDate = this.getDateObjFromUTCDate(startDate);
        const endMonth = item.endMonth,
            endDay = item.endDay,
            endDate = endMonth + '/' + endDay + '/' + currentYear;
        obj.end = this.getDateObjFromUTC(endDate);
        obj.endDate = this.getDateObjFromUTCDate(endDate);
        obj.commissionable = item.commissionable;
        obj.startYear = item.startYear ? item.startYear.def : '';
        obj.endYear = item.endYear ? item.endYear.def : '';
        return obj;
    }

    public getPeriodSegment(region: any) {
        if (region) {
            return this.http.get(this.envUrl.toString() + '/periodsegmentdef?regionCode=' + region).toPromise();
        } else {
            return this.http.get(this.envUrl.toString() + '/periodsegmentdef').toPromise();
        }
    }

    public postPeriodSegment(region: any, payload: any) {
        if (region) {
            return this.http.post(this.envUrl.toString() + '/periodsegmentdef?regionCode=' + region, payload).toPromise();
        } else {
            return this.http.post(this.envUrl.toString() + '/periodsegmentdef', payload).toPromise();
        }
    }

    public putPeriodSegment(region: any, payload: any) {
        if (region) {
            return this.http.put(this.envUrl.toString() + '/periodsegmentdef?regionCode=' + region, payload).toPromise();
        } else {
            return this.http.put(this.envUrl.toString() + '/periodsegmentdef', payload).toPromise();
        }
    }
}